import { Component, DestroyRef, effect, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { PipesModule } from 'src/app/core/pipes/pipes.module';
import { TWorkspacesById } from 'src/app/project/modules/workspace/workspace.model';
import { iconMap } from 'src/app/project/shared/enums/icon-map';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { EStore } from 'src/app/project/shared/enums/store.enum';
import { IconModule } from '../../icon/icon-module';
import { NewRelatedPointRowComponent } from '../new-related-point-row/new-related-point-row.component';
import { RelatedPointRowComponent } from '../related-point-row/related-point-row.component';
import { TRelatedPoint } from '../related-points.consts';

@Component({
  selector: 'pp-related-points',
  imports: [PipesModule, IconModule, NewRelatedPointRowComponent, RelatedPointRowComponent],
  templateUrl: './related-points.component.html',
  styleUrl: './related-points.component.scss',
})
export class RelatedPointsComponent {
  ppWorkspaceId = input.required<string>();
  ppPointId = input.required<string>();
  ppRelationships = input.required<TRelatedPoint[]>();
  ppAccountId = input.required<string>();
  ppIsNewPoint = input<boolean>(false);

  private relatedPointsByPointId: Record<string, TRelatedPoint[]> = {};
  expanded = true;
  iconMap = iconMap;
  EIconPath = EIconPath;
  addingNewRelation = false;
  canEditRelationsInCurrentWorkspace = false;
  private workspacesById: TWorkspacesById = {};
  private workspaces$: Observable<TWorkspacesById>;
  private destroyRef = inject(DestroyRef);

  get relatedPoints(): TRelatedPoint[] {
    return this.relatedPointsByPointId[this.ppPointId()] ?? [];
  }

  set relatedPoints(value: TRelatedPoint[]) {
    this.relatedPointsByPointId[this.ppPointId()] = value;
  }

  get excludedPointIds(): string[] {
    return [this.ppPointId(), ...this.relatedPoints.map((p) => p.pointId)];
  }

  constructor(
    private store: Store<{
      workspaces: TWorkspacesById;
    }>,
  ) {
    this.workspaces$ = this.store.pipe(select(EStore.WORKSPACES));

    effect(() => {
      const incomingRelations = this.ppRelationships();
      const pointId = this.ppPointId();
      const currentRelations = this.relatedPointsByPointId[pointId];

      if (incomingRelations?.length || !currentRelations?.length) {
        this.relatedPointsByPointId[pointId] = incomingRelations ?? [];
      }
    });
  }

  ngOnInit(): void {
    this.workspaces$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((workspaces) => {
      this.workspacesById = workspaces;

      this.canEditRelationsInCurrentWorkspace = this.hasRelationshipsEditPermission(
        this.ppWorkspaceId(),
      );
    });
  }

  canDeleteRelation(relation: TRelatedPoint): boolean {
    const canEditRelationsInTargetWorkspace = this.hasRelationshipsEditPermission(relation.siteId);

    return this.canEditRelationsInCurrentWorkspace && canEditRelationsInTargetWorkspace;
  }

  toggleExpand(): void {
    this.expanded = !this.expanded;
  }

  addNewRelation(): void {
    this.addingNewRelation = true;
  }

  cancelNewRelation(): void {
    this.addingNewRelation = false;
  }

  addRelationToList(relation: TRelatedPoint): void {
    this.relatedPoints.push(relation);
    this.addingNewRelation = false;
  }

  deleteRelation(relation: TRelatedPoint): void {
    this.relatedPoints = this.relatedPoints.filter(
      (item) => item.pointId !== relation.pointId || item.type !== relation.type,
    );
  }

  private hasRelationshipsEditPermission(siteId: string): boolean {
    const workspace = this.workspacesById[siteId];

    return workspace?.share?.advancedAccessLevels?.pointsRelationships?.permission?.edit ?? false;
  }
}
